import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Stack,
  Divider,
  Chip,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material'
import {
  Person as PersonIcon,
  Square as SquareIcon,
  KingBed as KingBedIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'
import { fetchRooms, type Room } from '../services/roomService'
import BookingContactModal from '../components/BookingContactModal'

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
}

const RoomsListPage = () => {
  const navigate = useNavigate()
  const { convertPrice } = useCurrency()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 })
  const [rooms, setRooms] = useState<Room[]>([])
  const [contactModalRoom, setContactModalRoom] = useState<Room | null>(null)

  useEffect(() => {
    fetchRooms().then(setRooms)
  }, [])

  return (
    <Box>
      <BookingContactModal
        open={contactModalRoom !== null}
        onClose={() => setContactModalRoom(null)}
        roomName={contactModalRoom?.name}
      />

      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 5, md: 8 },
          textAlign: 'center',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3 }}
          >
            ACCOMMODATIONS
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mt: 1,
              mb: 2,
              background: (theme) =>
                `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Our Rooms &amp; Suites
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.8, fontSize: { xs: '0.95rem', md: '1.05rem' } }}
          >
            Choose from our thoughtfully designed rooms, each offering a unique blend
            of comfort, elegance, and modern amenities.
          </Typography>
        </Container>
      </Box>

      {/* Room Cards */}
      <Box
        ref={ref}
        component={motion.div}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={staggerContainer}
        sx={{ py: { xs: 5, md: 8 }, bgcolor: 'background.default' }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {rooms.map((room) => (
              <Grid
                item
                key={room.id}
                xs={12}
                sm={6}
                lg={3}
                component={motion.div}
                variants={fadeInUp}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => navigate(`/rooms/${room.id}`)}
                >
                  {/* Image */}
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="230"
                      image={room.images[0]}
                      alt={room.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'success.main',
                        color: 'white',
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 2,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      }}
                    >
                      Available
                    </Box>
                  </Box>

                  {/* Content */}
                  <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: { xs: '1rem', md: '1.15rem' },
                      }}
                    >
                      {room.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, lineHeight: 1.6, flexGrow: 1 }}
                    >
                      {room.description}
                    </Typography>

                    {/* Capacity & size */}
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <PersonIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {room.capacity.adults} Adults
                          {room.capacity.children > 0 && ` + ${room.capacity.children} Child`}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <SquareIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {room.size.squareFeet} sq ft
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <KingBedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {room.beds[0].count} {room.beds[0].type} bed
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Amenity chips */}
                    <Stack direction="row" sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                      {room.amenities.slice(0, 4).map((amenity) => (
                        <Chip
                          key={amenity}
                          label={amenity}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1, fontSize: '0.68rem', borderColor: 'rgba(0,0,0,0.12)' }}
                        />
                      ))}
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Price + actions */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700, lineHeight: 1 }}>
                          {convertPrice(room.basePrice)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          per night + GST
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/rooms/${room.id}`)
                          }}
                          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 500 }}
                        >
                          Details
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            setContactModalRoom(room)
                          }}
                          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 2 }}
                        >
                          Book Now
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  )
}

export default RoomsListPage
